import {Draft} from 'immer';
import cloneDeep from 'lodash/cloneDeep';

import * as BookmarkTypes from 'common/types/reports/bookmark';

import type {BookmarkDragOperationMetadata} from './types';

export function getBehaviorForDragDropOperation(
  clause: Draft<BookmarkTypes.BehaviorShowClause>,
  data: BookmarkDragOperationMetadata,
) {
  if (
    clause.behavior?.type === BookmarkTypes.MetricType.Cohort ||
    clause.behavior?.type === BookmarkTypes.MetricType.People
  ) {
    return clause.behavior;
  }

  if (Array.isArray(data.behaviorIndex)) {
    // when behaviorIndex is an array, we are dropping a filter onto a funnel comparison event
    return clause.behavior!.behaviors![data.behaviorIndex[0]].behaviors![data.behaviorIndex[1]]!;
  } else if (typeof data.behaviorIndex === `number`) {
    return clause.behavior!.behaviors![data.behaviorIndex]!;
  }

  throw new Error(`Invalid clause type for filter operation: ${data.type}`);
}

/**
 * Generates a filtered clone of the given filter object.
 * Removes `isInherited` and `isHidden` properties if `isInherited` is present and true.
 *
 * @param filter - The filter object to clone and process.
 * @returns An object containing the cloned result and a boolean indicating if forceClone was applied.
 */
export function generateFilter(filter: Obj, opts: {isCloning?: boolean} = {}) {
  if (!filter) {
    return null;
  }

  const result = cloneDeep(filter);

  // forces inherited filters to be cloned, example: pinned filters in boards must be duplicated
  const forceClone = `isInherited` in filter && filter.isInherited;
  if (opts.isCloning || forceClone) {
    delete result.isInherited;
    delete result.isHidden;
  }

  return {
    result,
    forceClone,
  };
}

/** Makes sure all filters are added with a defined determiner */
export function addFilterToTargetBehavior(
  behavior: BookmarkTypes.Behavior | BookmarkTypes.SubBehavior,
  targetFilter: Obj | null,
) {
  behavior.filters ||= [];
  if (targetFilter) {
    behavior.filters!.push(targetFilter);
  }
  behavior.filters = behavior.filters!.map((filter) => ({
    ...filter,
    determiner: behavior.filtersDeterminer,
  }));
}
